import { Direction, EventTimeline, EventType, Filter, type MatrixClient, type MatrixEvent, MsgType, type Room, RoomEvent, TimelineWindow } from 'matrix-js-sdk'
import type { IChat, IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import {
  type MatrixUserContext,
  formatMatrixDisplayName,
  resolveMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixUserId'
import {
  enrichMatrixMessages,
  resolveMatrixSenderProfile,
} from '@/modules/chat-matrix-fast/services/matrixProfileCache'
import {
  CHAT_TYPES,
  MATRIX_CHAT_DELETE_FOR_PEER_EVENT,
  MATRIX_FORWARD_CONTENT_KEY,
  hasLastMessagePreview,
  isMatrixHiddenUser,
} from '@/modules/chat-matrix-fast/constants/chat'
import {
  collectRoomMatrixEvents,
  getMatrixMessagesFromRoom,
  hashToNumber,
  looksLikeMatrixEventId,
  mapMatrixEventsToMessages,
  resolveMatrixEventCollectionKey,
  resolveMatrixMessageContent,
} from '@/modules/chat-matrix-fast/services/matrixEventMapper'
import {
  getMatrixEventRoomMeta,
  isMatrixDirectRoom,
  roomToChat,
} from '@/modules/chat-matrix-fast/services/matrixRoomMapper'
import {
  getForgottenRoomIds,
  isForgottenRoom,
  rememberForgottenRoom,
  restoreForgottenRoom,
} from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import { useUserStore } from '@/modules/user/stores/userStore'
import {
  ensureMatrixSession,
  getMatrixSession,
} from '@/modules/chat-matrix-fast/matrixSession'
import { toAuthenticatedMatrixMediaUrl } from '@/modules/chat-matrix-fast/services/matrixMediaUrl'
import {
  enrichMessagesWithReadReceipts,
} from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'
import {
  getMatrixHttpStatus,
  isMatrixSyncAuthFailure,
  isMatrixSyncAuthRecoveryBlocked,
  readMatrixSyncError,
  recoverMatrixSyncAfterAuthError,
  withMatrixAuthRetry,
} from '@/modules/chat-matrix-fast/services/matrixAuthRecovery'
import {
  getMatrixClient,
  getMatrixSyncAuthRecoveryBlockReason,
  isMatrixSyncReady,
  peekMatrixClient,
  startMatrixClient,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import type { ChatMemberResponse } from '@/modules/chat-matrix-fast/services/chatApiService'
import { phoneFromMatrixLocalpart } from '@/modules/chat-matrix-fast/services/matrixUserId'
import { getCachedMatrixPresence, trackMatrixPresence } from '@/modules/chat-matrix-fast/services/matrixPresenceService'
import {
  applyMatrixSdkLogLevel,
  chatDebugLog,
  matrixSyncDebugLog,
  messagesDebugLog,
  recordMatrixHistoryLoadPath,
  searchDebugLog,
  summarizeChatsForLog,
  summarizeLastMessageForLog,
  summarizeSearchBodyForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'

applyMatrixSdkLogLevel()

function isActiveMatrixMembership(membership: string | undefined): boolean {
  return membership === 'join' || membership === 'invite'
}

function resolveMatrixRoomId(chat: IChat, matrixClient: MatrixClient): string | undefined {
  if (chat.matrix_room_id) {
    return chat.matrix_room_id
  }

  return matrixClient
    .getRooms()
    .find(room => hashToNumber(room.roomId) === chat.id)
    ?.roomId
}

function findMatrixRoomByChatId(
  matrixClient: MatrixClient,
  chatId: number,
): Room | undefined {
  return matrixClient.getRooms().find(room => {
    const eventMeta = getMatrixEventRoomMeta(room)
    if (eventMeta?.chat_id === chatId) {
      return true
    }

    return hashToNumber(room.roomId) === chatId
  })
}

export async function resolveMatrixRoomIdByChatId(chatId: number): Promise<string | undefined> {
  const matrixClient = await startMatrixClient()
  return findMatrixRoomByChatId(matrixClient, chatId)?.roomId
}

export async function fetchChatFromMatrix(
  chatId: number,
  fallback?: Partial<IChat> | null,
): Promise<IChat> {
  const matrixClient = await startMatrixClient()
  const myUserId = matrixClient.getUserId()

  let room: Room | undefined

  if (fallback?.matrix_room_id) {
    room = matrixClient.getRoom(fallback.matrix_room_id) ?? undefined
  }

  if (!room) {
    room = findMatrixRoomByChatId(matrixClient, chatId)
  }

  if (!room) {
    const roomId = await resolveMatrixRoomIdByChatId(chatId)
    if (roomId) {
      room = matrixClient.getRoom(roomId) ?? undefined
    }
  }

  if (!room) {
    if (fallback?.id || fallback?.matrix_room_id) {
      return {
        id: fallback.id ?? chatId,
        type: fallback.type ?? CHAT_TYPES.DIALOG,
        title: fallback.title ?? '',
        description: fallback.description ?? '',
        icon: fallback.icon ?? null,
        owner: fallback.owner ?? '',
        members: fallback.members ?? [],
        created_at: fallback.created_at ?? new Date().toISOString(),
        unread_count: fallback.unread_count ?? 0,
        matrix_room_id: fallback.matrix_room_id,
        event_url: fallback.event_url ?? null,
        total_members: fallback.total_members,
      } as IChat
    }

    throw new Error(`Chat room not found for chat ${chatId}`)
  }

  let chat = roomToChat(room, myUserId, matrixClient)

  if (fallback?.id && fallback.id !== chat.id) {
    const sameRoom = fallback.matrix_room_id === room.roomId
      || hashToNumber(room.roomId) === fallback.id
      || getMatrixEventRoomMeta(room)?.chat_id === fallback.id

    if (sameRoom || fallback.type === CHAT_TYPES.EVENT || fallback.event_url) {
      chat = {
        ...chat,
        id: fallback.id,
        type: fallback.type ?? chat.type,
        event_url: fallback.event_url ?? chat.event_url ?? null,
        title: fallback.title || chat.title,
        total_members: fallback.total_members ?? chat.total_members,
      }
    }
  }

  return chat
}

async function waitUntilNotJoined(
  matrixClient: MatrixClient,
  matrixRoomId: string,
  timeoutMs = 15_000,
): Promise<void> {
  const room = matrixClient.getRoom(matrixRoomId)
  if (!room || room.getMyMembership() !== 'join') {
    return
  }

  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      room.removeListener(RoomEvent.MyMembership, onMembership)
      reject(new Error('Не удалось покинуть чат'))
    }, timeoutMs)

    const onMembership = () => {
      if (room.getMyMembership() !== 'join') {
        window.clearTimeout(timeout)
        room.removeListener(RoomEvent.MyMembership, onMembership)
        resolve()
      }
    }

    room.on(RoomEvent.MyMembership, onMembership)
  })
}

export function roomIdToChatId(roomId: string): number {
  return hashToNumber(roomId)
}

// Synthetic id from matrix_room_id — not valid for GET /api/matrix_chat/chat/{id}/ (403).
export function isSyntheticMatrixChatId(
  chatId: number,
  hints?: { matrix_room_id?: string | null },
): boolean {
  if (hints?.matrix_room_id && chatId === roomIdToChatId(hints.matrix_room_id)) {
    return true
  }

  try {
    const matrixClient = getMatrixClient()
    const room = matrixClient.getRooms().find(item => hashToNumber(item.roomId) === chatId)
    if (!room) {
      return false
    }

    const eventMeta = getMatrixEventRoomMeta(room)
    if (eventMeta?.chat_id != null && eventMeta.chat_id === chatId) {
      return false
    }

    return eventMeta?.chat_id == null
  } catch {
    return false
  }
}
function getCurrentMatrixUserContext(): MatrixUserContext {
  try {
    const session = getMatrixSession()
    const userStore = useUserStore()

    return {
      employeeId: userStore.user?.id || userStore.user?.uuid,
      matrixUserId: session?.userId,
      logtoSub: userStore.user?.sub,
      phoneNumber: userStore.user?.phone_number,
    }
  } catch {
    return {}
  }
}

export function getCurrentMatrixUserId(): string | null {
  return getMatrixSession()?.userId ?? null
}

const MATRIX_ADMIN_POWER_LEVEL = 50

// Matrix admin: creator or power level >= 50
export function isMatrixRoomAdmin(roomId: string): boolean {
  const myUserId = getCurrentMatrixUserId()
  const client = peekMatrixClient()
  if (!myUserId || !roomId || !client) {
    return false
  }

  const room = client.getRoom(roomId)
  if (!room) {
    return false
  }

  const creator = room.getCreator?.() ?? ''
  if (myUserId === creator) {
    return true
  }

  return (room.getMember(myUserId)?.powerLevel ?? 0) >= MATRIX_ADMIN_POWER_LEVEL
}

export function isChatAccessibleInMatrix(chat: Pick<IChat, 'matrix_room_id'>): boolean {
  const roomId = chat.matrix_room_id?.trim()
  if (!roomId) {
    return true
  }

  try {
    const room = getMatrixClient().getRoom(roomId)
    if (!room) {
      return true
    }

    const membership = room.getMyMembership()
    return membership === 'join' || membership === 'invite'
  } catch {
    return true
  }
}

const MATRIX_TIMELINE_MESSAGE_EVENT_TYPES = [
  EventType.RoomMessage,
  EventType.RoomMessageEncrypted,
] as const

function createMatrixMessagesTimelineFilter(matrixClient?: MatrixClient): Filter {
  const filter = new Filter(matrixClient?.getUserId() ?? getMatrixClient().getUserId())
  filter.setDefinition({
    room: {
      timeline: {
        types: [...MATRIX_TIMELINE_MESSAGE_EVENT_TYPES],
      },
    },
  })
  return filter
}

function summarizeMatrixEventsForDebug(events: MatrixEvent[]) {
  const typeCounts = new Map<string, number>()
  const samples: Array<{
    type: string
    eventId: string | undefined
    msgtype: string
    hasBody: boolean
    hasUrl: boolean
    isRedacted: boolean
    relType: string | undefined
  }> = []

  for (const event of events) {
    const type = event.getType()
    typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1)

    if (samples.length < 8) {
      const content = event.getContent()
      samples.push({
        type,
        eventId: event.getId(),
        msgtype: String(content?.msgtype || ''),
        hasBody: Boolean(String(content?.body || content?.formatted_body || '').trim()),
        hasUrl: Boolean(content?.url),
        isRedacted: event.isRedacted(),
        relType: event.getRelation()?.rel_type,
      })
    }
  }

  const eventTypesSummary = [...typeCounts.entries()]
    .map(([type, count]) => `${type}×${count}`)
    .join(', ')

  return {
    eventTypesSummary,
    typeCounts: Object.fromEntries(typeCounts),
    samples,
  }
}

export function getMatrixRoomLastMessage(room: Room): IMessage | undefined {
  const messages = getMatrixMessagesFromRoom(room)
  return messages[messages.length - 1]
}

export function hasMatrixMessagePreview(message: IMessage | null | undefined): boolean {
  return hasLastMessagePreview(message)
}

// Topic sidebar preview: timeline first, else one filtered /messages (403 without join).
export async function fetchMatrixRoomLastMessagePreview(
  roomId: string,
): Promise<IMessage | null> {
  const normalizedRoomId = roomId.trim()
  if (!normalizedRoomId) {
    return null
  }

  if (isMatrixRoomHistoryForbidden(normalizedRoomId)) {
    return null
  }

  return withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    let room = matrixClient.getRoom(normalizedRoomId) ?? undefined
    if (!room) {
      room = await waitForMatrixRoom(matrixClient, normalizedRoomId, 2500) ?? undefined
    }

    if (room?.getMyMembership() === 'join') {
      const local = getMatrixRoomLastMessage(room)
      if (hasMatrixMessagePreview(local)) {
        return local ?? null
      }

      try {
        await forceLoadRoomHistoryFromServer(
          matrixClient,
          room,
          MATRIX_QUICK_SCROLLBACK_LIMIT,
        )
        const afterWarm = getMatrixRoomLastMessage(room)
        if (hasMatrixMessagePreview(afterWarm)) {
          clearMatrixRoomHistoryForbidden(normalizedRoomId)
          return afterWarm ?? null
        }
      } catch (error) {
        if (getMatrixHttpStatus(error) === 403) {
          rememberMatrixRoomHistoryForbidden(normalizedRoomId)
        }
      }

      if (isMatrixRoomHistoryForbidden(normalizedRoomId)) {
        return getMatrixRoomLastMessage(room) ?? null
      }
    }

    try {
      const messages = await fetchMessagesDirectFromServer(
        matrixClient,
        normalizedRoomId,
        MATRIX_QUICK_SCROLLBACK_LIMIT,
        { useMessageFilter: true },
      )
      const last = messages[messages.length - 1]
      return hasMatrixMessagePreview(last) ? last : null
    } catch (error) {
      if (getMatrixHttpStatus(error) === 403) {
        rememberMatrixRoomHistoryForbidden(normalizedRoomId)
      }
      return room ? (getMatrixRoomLastMessage(room) ?? null) : null
    }
  })
}

/** Превью темы ещё нельзя считать готовым: нет room в SDK или sync не готов. */
export function isMatrixRoomPreviewPending(roomId: string | null | undefined): boolean {
  const normalizedRoomId = roomId?.trim()
  if (!normalizedRoomId) {
    return false
  }

  try {
    const matrixClient = getMatrixClient()
    const syncState = matrixClient.getSyncState()
    if (!syncState || syncState === 'STOPPED' || syncState === 'ERROR') {
      return true
    }

    if (!isMatrixSyncReady(matrixClient)) {
      return true
    }

    const room = matrixClient.getRoom(normalizedRoomId)
    if (!room) {
      return true
    }

    const membership = room.getMyMembership()
    if (membership !== 'join') {
      return true
    }

    return false
  } catch {
    return true
  }
}

export async function fetchMatrixChats(): Promise<IChat[]> {
  return withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    const forgotten = getForgottenRoomIds()
    const myUserId = matrixClient.getUserId()

    const allRooms = matrixClient.getRooms()
    const joinedRooms = allRooms
      .filter(room => room.getMyMembership() === 'join')
      .filter(room => !forgotten.has(room.roomId))

    chatDebugLog('fetchMatrixChats → rooms from Matrix client', {
      totalRoomsInClient: allRooms.length,
      joinedRoomsCount: joinedRooms.length,
      forgottenRoomsCount: forgotten.size,
      joinedRooms: joinedRooms.map(room => ({
        roomId: room.roomId,
        name: room.name,
        membership: room.getMyMembership(),
      })),
    })

    for (const room of joinedRooms) {
      if (roomHasPeerDeleteRequest(room, myUserId)) {
        await leaveAndForgetMatrixRoom(room.roomId)
      }
    }

    const chats = matrixClient
      .getRooms()
      .filter(room => room.getMyMembership() === 'join')
      .filter(room => !getForgottenRoomIds().has(room.roomId))
      .map(room => roomToChat(room, myUserId, matrixClient))

    const result = [...chats]

    result.sort(
      (a, b) => Date.parse(b.last_message?.created_at ?? b.created_at)
        - Date.parse(a.last_message?.created_at ?? a.created_at),
    )

    chatDebugLog('fetchMatrixChats → result', {
      resultCount: result.length,
      chats: summarizeChatsForLog(result, result.length),
    })

    return result
  })
}

export function isPeerDeleteRequestEvent(
  event: MatrixEvent,
  myUserId: string | null | undefined,
): boolean {
  return event.getType() === MATRIX_CHAT_DELETE_FOR_PEER_EVENT
    && Boolean(event.getSender())
    && event.getSender() !== myUserId
}

function roomHasPeerDeleteRequest(
  room: Room,
  myUserId: string | null | undefined,
): boolean {
  const timeline = room.timeline ?? []

  for (let index = timeline.length - 1; index >= 0; index -= 1) {
    const event = timeline[index]
    if (isPeerDeleteRequestEvent(event, myUserId)) {
      return true
    }
  }

  return false
}

export async function ensureMatrixSyncAlive(): Promise<void> {
  if (isMatrixSyncAuthRecoveryBlocked()) {
    matrixSyncDebugLog('ensureMatrixSyncAlive → skipped, auth recovery circuit open', {
      blockReason: getMatrixSyncAuthRecoveryBlockReason(),
    })
    return
  }

  const session = await ensureMatrixSession()
  if (!session) {
    return
  }

  const matrixClient = getMatrixClient()
  matrixClient.setAccessToken(session.accessToken)

  const syncState = matrixClient.getSyncState()
  if (syncState === 'ERROR' && isMatrixSyncAuthFailure(readMatrixSyncError(matrixClient))) {
    await recoverMatrixSyncAfterAuthError(matrixClient, 'ensure-alive-auth-error')
    return
  }

  if (!isMatrixSyncReady(matrixClient) && !matrixClient.clientRunning) {
    await startMatrixClient()
  }
}

function summarizeRoomTimelineDebug(room: Room) {
  const timelineEvents = room.getLiveTimeline().getEvents()
  const collectedEvents = collectRoomMatrixEvents(room)

  return {
    timelineEvents: timelineEvents.length,
    collectedEvents: collectedEvents.length,
    timeline: summarizeMatrixEventsForDebug(timelineEvents),
    collected: summarizeMatrixEventsForDebug(collectedEvents),
  }
}

const MATRIX_SCROLLBACK_LIMIT_DIALOG = 30
const MATRIX_SCROLLBACK_LIMIT_GROUP = 50
/** Лёгкий warm-up для openTopic/openChat: cold topic rooms часто без m.room.message в sync. */
const MATRIX_QUICK_SCROLLBACK_LIMIT = 20
const MATRIX_SCROLLBACK_MAX_ROUNDS = 1
/** Пауза перед повторной попыткой scrollback/preview для комнаты с 403 (нет join) */
const MATRIX_SCROLLBACK_FORBIDDEN_COOLDOWN_MS = 60_000

const scrollbackForbiddenAt = new Map<string, number>()

export function isMatrixRoomHistoryForbidden(roomId: string): boolean {
  const forbiddenAt = scrollbackForbiddenAt.get(roomId)
  return forbiddenAt !== undefined
    && Date.now() - forbiddenAt < MATRIX_SCROLLBACK_FORBIDDEN_COOLDOWN_MS
}

export function rememberMatrixRoomHistoryForbidden(roomId: string): void {
  scrollbackForbiddenAt.set(roomId, Date.now())
}

export function clearMatrixRoomHistoryForbidden(roomId: string): void {
  scrollbackForbiddenAt.delete(roomId)
}

function countRoomMessages(room: Room): number {
  return getMatrixMessagesFromRoom(room).length
}

function collectRoomMatrixEventKeys(room: Room): Set<string> {
  const keys = new Set<string>()
  for (const event of collectRoomMatrixEvents(room)) {
    const key = resolveMatrixEventCollectionKey(event)
    if (key) {
      keys.add(key)
    }
  }
  return keys
}

function collectDeltaRoomMatrixEvents(room: Room, beforeKeys: Set<string>): MatrixEvent[] {
  return collectRoomMatrixEvents(room).filter(event => {
    const key = resolveMatrixEventCollectionKey(event)
    return Boolean(key && !beforeKeys.has(key))
  })
}

export function roomTimelineRepublishFingerprint(room: Room): string {
  const events = collectRoomMatrixEvents(room)
  const last = events[events.length - 1]
  const lastKey = last ? resolveMatrixEventCollectionKey(last) ?? '' : ''
  return `${events.length}:${lastKey}`
}

export async function readTimelineMessages(
  matrixClient: MatrixClient,
  room: Room,
  options?: { messages?: IMessage[] },
): Promise<IMessage[]> {
  const base = options?.messages ?? getMatrixMessagesFromRoom(room)
  const withProfiles = await enrichMatrixMessages(matrixClient, base, room)
  return enrichMessagesWithReadReceipts(matrixClient, room, withProfiles)
}

function historyLoadContext(room: Room, extra?: Record<string, unknown>) {
  return {
    roomId: room.roomId,
    timelineEvents: room.getLiveTimeline().getEvents().length,
    messageCount: countRoomMessages(room),
    ...extra,
  }
}

async function requestMatrixRoomMessagesBackward(
  matrixClient: MatrixClient,
  roomId: string,
  token: string | null | undefined,
  limit: number,
  filter?: Filter,
) {
  return matrixClient.createMessagesRequest(
    roomId,
    token ?? null,
    limit,
    Direction.Backward,
    filter,
  )
}

function injectMatrixEventsIntoLiveTimeline(
  matrixClient: MatrixClient,
  room: Room,
  matrixEvents: MatrixEvent[],
): void {
  if (!matrixEvents.length) {
    return
  }

  const [timelineEvents, threadedEvents, unknownRelations] = room.partitionThreadedEvents(matrixEvents)
  matrixClient.processAggregatedTimelineEvents(room, timelineEvents)
  room.addEventsToTimeline(timelineEvents, true, true, room.getLiveTimeline())
  matrixClient.processThreadEvents(room, threadedEvents, true)
  unknownRelations.forEach(event => room.relations.aggregateChildEvent(event))
}

/** Прямой запрос /messages, когда scrollback пропускает загрузку (paginationToken === null при пустом timeline). */
async function forceLoadRoomHistoryFromServer(
  matrixClient: MatrixClient,
  room: Room,
  limit: number,
): Promise<void> {
  if (countRoomMessages(room) > 0) {
    return
  }

  if (room.getMyMembership() !== 'join' || isMatrixRoomHistoryForbidden(room.roomId)) {
    return
  }

  const timelineFilter = createMatrixMessagesTimelineFilter(matrixClient)
  const paginationToken = room.getLiveTimeline().getPaginationToken(EventTimeline.BACKWARDS)
    ?? room.oldState.paginationToken
    ?? null

  try {
    const response = await requestMatrixRoomMessagesBackward(
      matrixClient,
      room.roomId,
      paginationToken,
      limit,
      timelineFilter,
    )

    if (!response.chunk.length) {
      matrixSyncDebugLog('forceLoadRoomHistoryFromServer → empty chunk', {
        roomId: room.roomId,
        paginationToken: paginationToken ?? null,
      })
      return
    }

    const matrixEvents = response.chunk.map(matrixClient.getEventMapper())
    injectMatrixEventsIntoLiveTimeline(matrixClient, room, matrixEvents)

    room.oldState.paginationToken = response.end ?? null
    if (response.chunk.length === 0) {
      room.oldState.paginationToken = null
    }

    recordMatrixHistoryLoadPath('forceLoad', historyLoadContext(room, {
      chunkSize: response.chunk.length,
    }))

    matrixSyncDebugLog('forceLoadRoomHistoryFromServer → loaded', {
      roomId: room.roomId,
      chunkSize: response.chunk.length,
      messageCountAfter: countRoomMessages(room),
    })
  } catch (error) {
    if (getMatrixHttpStatus(error) === 403) {
      rememberMatrixRoomHistoryForbidden(room.roomId)
    }
    matrixSyncDebugLog('forceLoadRoomHistoryFromServer → failed', {
      roomId: room.roomId,
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

async function ensureMatrixRoomHistoryLoaded(
  matrixClient: MatrixClient,
  room: Room,
): Promise<void> {
  if (room.getMyMembership() !== 'join' || isMatrixRoomHistoryForbidden(room.roomId)) {
    return
  }

  const limit = isMatrixDirectRoom(room)
    ? MATRIX_SCROLLBACK_LIMIT_DIALOG
    : MATRIX_SCROLLBACK_LIMIT_GROUP

  // Экран уже заполнен — старое догружает пагинация при скролле вверх.
  if (countRoomMessages(room) >= limit) {
    return
  }

  for (let round = 0; round < MATRIX_SCROLLBACK_MAX_ROUNDS; round += 1) {
    const paginationToken = room.getLiveTimeline().getPaginationToken(EventTimeline.BACKWARDS)
    if (!paginationToken) {
      clearMatrixRoomHistoryForbidden(room.roomId)
      break
    }

    const beforeScroll = countRoomMessages(room)
    try {
      await matrixClient.scrollback(room, limit)
      clearMatrixRoomHistoryForbidden(room.roomId)
      if (countRoomMessages(room) > beforeScroll) {
        recordMatrixHistoryLoadPath('scrollback', historyLoadContext(room, { phase: 'ensureHistory' }))
      }
    } catch (error) {
      if (getMatrixHttpStatus(error) === 403) {
        // Сервер запрещает пагинацию для этой комнаты (например, устаревший membership) —
        // не долбим /messages на каждый sync-тик, ждём кулдаун.
        rememberMatrixRoomHistoryForbidden(room.roomId)
      }
      break
    }
  }

  if (countRoomMessages(room) === 0) {
    await forceLoadRoomHistoryFromServer(matrixClient, room, limit)
  }
}

async function ensureThreadHistoriesLoaded(matrixClient: MatrixClient, room: Room): Promise<void> {
  if (!matrixClient.supportsThreads()) {
    return
  }

  const pending = room.getThreads().filter(thread => thread.events.length <= 1)
  const THREAD_HISTORY_CONCURRENCY = 3
  let nextIndex = 0
  const worker = async () => {
    while (nextIndex < pending.length) {
      const thread = pending[nextIndex]
      nextIndex += 1
      try {
        await matrixClient.paginateEventTimeline(thread.liveTimeline, {
          backwards: true,
          limit: MATRIX_SCROLLBACK_LIMIT_DIALOG,
        })
      } catch {
        // История треда может быть недоступна — не блокируем основной чат
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(THREAD_HISTORY_CONCURRENCY, pending.length) }, () => worker()),
  )
}

/** /threads на комнату — не чаще раза в TTL: дальше SDK ведёт треды по sync. */
const THREAD_CATALOG_TTL_MS = 5 * 60_000
const threadCatalogLoadedAt = new Map<string, number>()

/** Fallback: читаем /messages напрямую, если SDK-разбиение по threads не заполнило timeline. */
async function fetchMessagesDirectFromServer(
  matrixClient: MatrixClient,
  roomId: string,
  limit: number,
  options?: { useMessageFilter?: boolean },
): Promise<IMessage[]> {
  if (isMatrixRoomHistoryForbidden(roomId)) {
    return []
  }

  const room = matrixClient.getRoom(roomId)
  if (!room || room.getMyMembership() !== 'join') {
    // Без join Synapse отвечает 403 («room previews are disabled»).
    return []
  }

  const useMessageFilter = options?.useMessageFilter !== false
  const collected: MatrixEvent[] = []
  const seen = new Set<string>()
  let token: string | null | undefined
  const timelineFilter = useMessageFilter
    ? createMatrixMessagesTimelineFilter(matrixClient)
    : undefined

  try {
    for (let page = 0; page < MATRIX_SCROLLBACK_MAX_ROUNDS; page += 1) {
      const response = await requestMatrixRoomMessagesBackward(
        matrixClient,
        roomId,
        token,
        limit,
        timelineFilter,
      )

      if (!response.chunk.length) {
        break
      }

      for (const raw of response.chunk) {
        const event = matrixClient.getEventMapper()(raw)
        const key = resolveMatrixEventCollectionKey(event)
        if (!key || seen.has(key)) {
          continue
        }
        seen.add(key)
        collected.unshift(event)
      }

      token = response.end ?? undefined
      if (!token) {
        break
      }
    }
  } catch (error) {
    if (getMatrixHttpStatus(error) === 403) {
      rememberMatrixRoomHistoryForbidden(roomId)
    }
    chatDebugLog('fetchMessagesDirectFromServer → failed', {
      roomId,
      error: error instanceof Error ? error.message : String(error),
    })
    return []
  }

  // Превью сайдбара читает live timeline — без inject сообщения есть в UI, но lastMessage пустой.
  if (collected.length > 0 && countRoomMessages(room) === 0) {
    try {
      const matrixEvents = [...collected].reverse()
      injectMatrixEventsIntoLiveTimeline(matrixClient, room, matrixEvents)
      clearMatrixRoomHistoryForbidden(roomId)
    } catch {
      // mapping ниже всё равно вернёт сообщения в ленту
    }
  }

  const messages = mapMatrixEventsToMessages(collected)
  if (messages.length > 0) {
    recordMatrixHistoryLoadPath('direct', {
      roomId,
      timelineEvents: room.getLiveTimeline().getEvents().length,
      messageCount: messages.length,
      useMessageFilter,
      rawEvents: collected.length,
    })
  }
  const debug = collected.length > 0 ? summarizeMatrixEventsForDebug(collected) : null
  chatDebugLog('fetchMessagesDirectFromServer', {
    roomId,
    useMessageFilter,
    rawEvents: collected.length,
    messages: messages.length,
    timelineMessages: countRoomMessages(room),
    ...(debug && messages.length === 0
      ? {
          eventTypesSummary: debug.eventTypesSummary,
          typeCounts: debug.typeCounts,
          samples: debug.samples,
        }
      : {}),
  })
  return messages
}

async function ensureMatrixThreadCatalogLoaded(
  matrixClient: MatrixClient,
  room: Room,
): Promise<void> {
  if (!matrixClient.supportsThreads()) {
    return
  }

  const loadedAt = threadCatalogLoadedAt.get(room.roomId)
  if (loadedAt && Date.now() - loadedAt < THREAD_CATALOG_TTL_MS) {
    return
  }

  try {
    await room.fetchRoomThreads()
    threadCatalogLoadedAt.set(room.roomId, Date.now())
  } catch (error) {
    matrixSyncDebugLog('ensureMatrixThreadCatalogLoaded → failed', {
      roomId: room.roomId,
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

async function warmColdRoomTimeline(
  matrixClient: MatrixClient,
  room: Room,
  limit: number = MATRIX_QUICK_SCROLLBACK_LIMIT,
): Promise<IMessage[]> {
  if (countRoomMessages(room) > 0) {
    return getMatrixMessagesFromRoom(room)
  }

  if (room.getMyMembership() !== 'join' || isMatrixRoomHistoryForbidden(room.roomId)) {
    return []
  }

  try {
    await matrixClient.scrollback(room, limit)
    clearMatrixRoomHistoryForbidden(room.roomId)
  } catch (error) {
    if (getMatrixHttpStatus(error) === 403) {
      rememberMatrixRoomHistoryForbidden(room.roomId)
      return []
    }
  }

  let messages = getMatrixMessagesFromRoom(room)
  if (messages.length > 0) {
    recordMatrixHistoryLoadPath('scrollback', historyLoadContext(room, { phase: 'warmCold' }))
    recordMatrixHistoryLoadPath('warm', historyLoadContext(room, {
      topicSatellite: true,
      via: 'scrollback',
    }))
    return messages
  }

  await forceLoadRoomHistoryFromServer(matrixClient, room, limit)
  messages = getMatrixMessagesFromRoom(room)
  if (messages.length > 0) {
    recordMatrixHistoryLoadPath('warm', historyLoadContext(room, {
      topicSatellite: true,
      via: 'forceLoad',
    }))
    return messages
  }

  messages = await fetchMessagesDirectFromServer(matrixClient, room.roomId, limit, {
    useMessageFilter: true,
  })
  if (messages.length > 0) {
    recordMatrixHistoryLoadPath('warm', historyLoadContext(room, {
      topicSatellite: true,
      via: 'direct',
    }))
  }
  return messages
}

export async function getEnrichedMatrixMessagesFromRoom(
  matrixClient: MatrixClient,
  room: Room,
  options?: { loadHistory?: boolean; warmColdTimeline?: boolean },
): Promise<IMessage[]> {
  if (options?.loadHistory !== false) {
    if (countRoomMessages(room) === 0) {
      await ensureMatrixRoomHistoryLoaded(matrixClient, room)
    }
    await ensureMatrixThreadCatalogLoaded(matrixClient, room)
    await ensureThreadHistoriesLoaded(matrixClient, room)
  }

  let messages = getMatrixMessagesFromRoom(room)

  // openTopic quick path: satellite topics often have empty live timeline until /messages.
  if (messages.length === 0 && options?.warmColdTimeline && options?.loadHistory === false) {
    messages = await warmColdRoomTimeline(matrixClient, room)
    if (messages.length > 0) {
      chatDebugLog('getEnrichedMatrixMessagesFromRoom · warm cold timeline', {
        roomId: room.roomId,
        messageCount: messages.length,
      })
    }
  }

  if (messages.length === 0 && options?.loadHistory !== false) {
    messages = await fetchMessagesDirectFromServer(
      matrixClient,
      room.roomId,
      isMatrixDirectRoom(room) ? MATRIX_SCROLLBACK_LIMIT_DIALOG : MATRIX_SCROLLBACK_LIMIT_GROUP,
      { useMessageFilter: true },
    )
  }

  if (messages.length === 0 && options?.loadHistory !== false) {
    await ensureMatrixThreadCatalogLoaded(matrixClient, room)
    await ensureThreadHistoriesLoaded(matrixClient, room)
    messages = getMatrixMessagesFromRoom(room)
  }

  if (messages.length === 0 && options?.loadHistory !== false) {
    messages = await fetchMessagesDirectFromServer(
      matrixClient,
      room.roomId,
      isMatrixDirectRoom(room) ? MATRIX_SCROLLBACK_LIMIT_DIALOG : MATRIX_SCROLLBACK_LIMIT_GROUP,
      { useMessageFilter: false },
    )
  }

  const debugPayload = {
    roomId: room.roomId,
    mainTimelineEvents: room.getLiveTimeline().getEvents().length,
    threadCount: room.client?.supportsThreads() ? room.getThreads().length : 0,
    messageCount: messages.length,
  }

  if (messages.length === 0) {
    chatDebugLog('getEnrichedMatrixMessagesFromRoom · empty', {
      ...debugPayload,
      ...summarizeRoomTimelineDebug(room),
    })
  } else {
    chatDebugLog('getEnrichedMatrixMessagesFromRoom', debugPayload)
    messagesDebugLog('getEnrichedMatrixMessagesFromRoom · loaded', {
      roomId: room.roomId,
      messageCount: messages.length,
      first: summarizeLastMessageForLog(messages[0]),
      last: summarizeLastMessageForLog(messages[messages.length - 1]),
    })
  }

  return readTimelineMessages(matrixClient, room, { messages })
}

/** Одна страница истории назад (скролл к верху ленты). */
export async function paginateMatrixRoomBackwards(
  roomId: string,
): Promise<{ messages: IMessage[]; hasMore: boolean; added: number }> {
  return withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    const room = matrixClient.getRoom(roomId)
    if (!room || room.getMyMembership() !== 'join' || isMatrixRoomHistoryForbidden(roomId)) {
      return { messages: [], hasMore: false, added: 0 }
    }

    const beforeKeys = collectRoomMatrixEventKeys(room)
    const beforeMessageCount = countRoomMessages(room)
    const limit = isMatrixDirectRoom(room)
      ? MATRIX_SCROLLBACK_LIMIT_DIALOG
      : MATRIX_SCROLLBACK_LIMIT_GROUP

    if (room.getLiveTimeline().getPaginationToken(EventTimeline.BACKWARDS)) {
      try {
        await matrixClient.scrollback(room, limit)
      } catch (error) {
        if (getMatrixHttpStatus(error) === 403) {
          rememberMatrixRoomHistoryForbidden(roomId)
        }
        return { messages: [], hasMore: false, added: 0 }
      }
    }

    const hasMore = Boolean(room.getLiveTimeline().getPaginationToken(EventTimeline.BACKWARDS))
    const added = countRoomMessages(room) - beforeMessageCount
    const deltaEvents = collectDeltaRoomMatrixEvents(room, beforeKeys)
    if (added <= 0 || deltaEvents.length === 0) {
      return { messages: [], hasMore, added: 0 }
    }

    recordMatrixHistoryLoadPath('scrollback', historyLoadContext(room, {
      phase: 'paginateOlder',
      added,
    }))

    const messages = await readTimelineMessages(matrixClient, room, {
      messages: mapMatrixEventsToMessages(deltaEvents),
    })
    return { messages, hasMore, added }
  })
}

function waitForMatrixRoom(
  matrixClient: MatrixClient,
  roomId: string,
  timeoutMs = 5000,
): Promise<Room | null> {
  const existing = matrixClient.getRoom(roomId)
  if (existing) {
    return Promise.resolve(existing)
  }

  return new Promise(resolve => {
    let settled = false

    const finish = (room: Room | null) => {
      if (settled) {
        return
      }

      settled = true
      window.clearTimeout(timer)
      matrixClient.removeListener('Room' as any, onRoom)
      resolve(room)
    }

    const onRoom = (room: Room) => {
      if (room.roomId === roomId) {
        finish(room)
      }
    }

    const timer = window.setTimeout(() => finish(null), timeoutMs)
    matrixClient.on('Room' as any, onRoom)

    const retry = matrixClient.getRoom(roomId)
    if (retry) {
      finish(retry)
    }
  })
}

export function peekMatrixRoomMessages(roomId: string | null | undefined): IMessage[] {
  const id = roomId?.trim()
  if (!id) {
    return []
  }

  const room = peekMatrixClient()?.getRoom(id)
  if (!room) {
    return []
  }

  return getMatrixMessagesFromRoom(room)
}

export function hasLocalMatrixRoomTimeline(roomId: string | null | undefined): boolean {
  return peekMatrixRoomMessages(roomId).length > 0
}

const MATRIX_JUMP_WINDOW_SIZE = 40

/** Read `m.in_reply_to` from a reply event already in the room timeline (no network). */
export function peekInReplyToEventId(
  roomId: string | null | undefined,
  replyEventId: string | null | undefined,
): string | undefined {
  if (!roomId?.trim() || !looksLikeMatrixEventId(replyEventId)) {
    return undefined
  }

  const room = peekMatrixClient()?.getRoom(roomId)
  const event = room?.findEventById(replyEventId)
  if (!event) {
    return undefined
  }

  const content = resolveMatrixMessageContent(event) || event.getContent()
  const relatesTo = content?.['m.relates_to']
  if (!relatesTo || typeof relatesTo !== 'object') {
    return undefined
  }

  const inReplyTo = (relatesTo as Record<string, unknown>)['m.in_reply_to']
  if (!inReplyTo || typeof inReplyTo !== 'object') {
    return undefined
  }

  const parentId = (inReplyTo as Record<string, unknown>).event_id
  return looksLikeMatrixEventId(parentId) ? parentId : undefined
}

export async function fetchMatrixMessagesAroundEvent(
  roomId: string,
  eventId: string,
  windowSize = MATRIX_JUMP_WINDOW_SIZE,
): Promise<IMessage[]> {
  if (!looksLikeMatrixEventId(eventId)) {
    return []
  }

  return withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    let room = matrixClient.getRoom(roomId) ?? undefined
    if (!room) {
      room = await waitForMatrixRoom(matrixClient, roomId, 800) ?? undefined
    }
    if (!room) {
      chatDebugLog('fetchMatrixMessagesAroundEvent · room missing', { roomId, eventId })
      return []
    }

    const timelineWindow = new TimelineWindow(
      matrixClient,
      room.getUnfilteredTimelineSet(),
      { windowLimit: Math.max(windowSize * 3, 120) },
    )

    try {
      await timelineWindow.load(eventId, windowSize)
    } catch (error) {
      chatDebugLog('fetchMatrixMessagesAroundEvent · load failed', {
        roomId,
        eventId,
        error: error instanceof Error ? error.message : String(error),
      })
      return []
    }

    const side = Math.max(8, Math.floor(windowSize / 2))
    await timelineWindow.paginate(EventTimeline.BACKWARDS, side).catch(() => false)
    await timelineWindow.paginate(EventTimeline.FORWARDS, side).catch(() => false)

    const windowEvents = timelineWindow.getEvents()
    const missingEvents = windowEvents.filter(event => {
      const id = event.getId()
      return id && !room.findEventById(id)
    })
    if (missingEvents.length > 0) {
      injectMatrixEventsIntoLiveTimeline(matrixClient, room, missingEvents)
    }

    const messages = await readTimelineMessages(matrixClient, room, {
      messages: mapMatrixEventsToMessages(windowEvents),
    })

    chatDebugLog('fetchMatrixMessagesAroundEvent · loaded', {
      roomId,
      eventId,
      messageCount: messages.length,
      injectedEvents: missingEvents.length,
    })

    return messages
  })
}

export async function fetchMatrixMessages(
  roomId: string,
  options?: { loadHistory?: boolean; warmColdTimeline?: boolean },
): Promise<IMessage[]> {
  return withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    let room = matrixClient.getRoom(roomId) ?? undefined
    if (!room) {
      // Короткий wait: раньше 5с × 2 (quick + history) давали пустой чат без логов.
      room = await waitForMatrixRoom(matrixClient, roomId, 800) ?? undefined
    }

    // Topic/parent rooms can sit in local «forgotten» after prune; restore so invite join works.
    if (!room && isForgottenRoom(roomId)) {
      restoreForgottenRoom(roomId)
      chatDebugLog('fetchMatrixMessages · restored forgotten room', { roomId })
    }

    // Client /join without invite → 403 "not invited" + rc_joins 429.
    // Membership is repaired by Django ensure/retry-structure (admin join).
    if (!room) {
      const membership = matrixClient.getRoom(roomId)?.getMyMembership()
      if (membership === 'invite') {
        try {
          await matrixClient.joinRoom(roomId)
          room = matrixClient.getRoom(roomId) ?? undefined
          if (!room) {
            room = await waitForMatrixRoom(matrixClient, roomId, 800) ?? undefined
          }
        } catch (error) {
          chatDebugLog('fetchMatrixMessages · joinRoom failed', {
            roomId,
            error: error instanceof Error ? error.message : String(error),
          })
        }
      }
    }

    if (!room) {
      chatDebugLog('fetchMatrixMessages · room missing', {
        roomId,
        forgotten: isForgottenRoom(roomId),
        loadHistory: options?.loadHistory !== false,
      })

      // Без Room в SDK пользователь не joined — /messages даст 403, не ходим.
      return []
    }

    return getEnrichedMatrixMessagesFromRoom(matrixClient, room, options)
  })
}

export interface MatrixMessageSearchHit {
  id: number
  matrixEventId: string
  content: string
  createdAt: string
  senderId: string
  senderName: string
  senderAvatar: string | null
  isMine: boolean
}

function normalizeMessageSearchText(value: string): string {
  return value
    .toLocaleLowerCase('ru-RU')
    .replace(/ё/g, 'е')
    .trim()
}

function messageToSearchHit(
  message: IMessage,
  myUserId: string | null,
): MatrixMessageSearchHit {
  const senderId = String(message.author_id || message.author || '')
  const matrixEventId = String(message.matrix_event_id || message.id)
  const createdBy = message.created_by

  return {
    id: typeof message.id === 'number' ? message.id : hashToNumber(matrixEventId),
    matrixEventId,
    content: String(message.content || '').trim(),
    createdAt: message.created_at,
    senderId,
    isMine: Boolean(myUserId && senderId === myUserId),
    senderName: message.author_name
      || createdBy?.full_name
      || [
        createdBy?.last_name,
        createdBy?.first_name,
        createdBy?.middle_name,
      ].filter(Boolean).join(' ')
      || senderId,
    senderAvatar: message.avatar
      || createdBy?.avatar
      || null,
  }
}

function messageMatchesSearchNeedle(message: IMessage, needle: string): boolean {
  if (message.is_deleted) {
    return false
  }
  const body = normalizeMessageSearchText(String(message.content || ''))
  return Boolean(body && body.includes(needle))
}

async function searchMatrixMessagesViaServer(
  matrixClient: MatrixClient,
  roomId: string,
  trimmedQuery: string,
  needle: string,
  myUserId: string | null,
): Promise<MatrixMessageSearchHit[]> {
  try {
    let results = await matrixClient.searchRoomEvents({
      term: trimmedQuery,
      filter: {
        rooms: [roomId],
      },
    })

    const hits: MatrixMessageSearchHit[] = []
    const seen = new Set<string>()
    const maxPages = 3

    for (let page = 0; page < maxPages; page += 1) {
      const roomEvents = results?.results ?? []
      for (const result of roomEvents) {
        const resultEvent = result?.context?.getEvent?.()
        if (!resultEvent) {
          continue
        }

        const mapped = mapMatrixEventsToMessages([resultEvent])
        for (const message of mapped) {
          if (!message.matrix_event_id || seen.has(message.matrix_event_id)) {
            continue
          }
          if (!messageMatchesSearchNeedle(message, needle)) {
            continue
          }
          seen.add(message.matrix_event_id)
          hits.push(messageToSearchHit(message, myUserId))
        }
      }

      if (!results?.next_batch) {
        break
      }
      results = await matrixClient.backPaginateRoomEventsSearch(results)
    }

    searchDebugLog('matrix · server search', {
      roomId,
      query: trimmedQuery,
      hitsCount: hits.length,
    })
    return hits
  } catch (error) {
    searchDebugLog('matrix · server search failed', {
      roomId,
      query: trimmedQuery,
      error: error instanceof Error ? error.message : String(error),
    })
    return []
  }
}

export async function searchMatrixMessagesInRoom(
  roomId: string,
  query: string,
): Promise<MatrixMessageSearchHit[]> {
  const trimmedQuery = query.trim()
  if (!roomId || !trimmedQuery) {
    searchDebugLog('matrix · skip (empty query or room)', {
      roomId: roomId || null,
      query,
      trimmedQuery,
    })
    return []
  }

  return withMatrixAuthRetry(async () => {
    const startedAt = performance.now()
    searchDebugLog('matrix · start', {
      roomId,
      query,
      trimmedQuery,
    })

    const matrixClient = await startMatrixClient()
    const myUserId = matrixClient.getUserId()
    let room = matrixClient.getRoom(roomId) ?? undefined
    if (!room) {
      room = await waitForMatrixRoom(matrixClient, roomId) ?? undefined
    }
    if (!room) {
      searchDebugLog('matrix · room not found', { roomId, query: trimmedQuery })
      return []
    }

    const timelineBefore = room.getLiveTimeline().getEvents().length
    const messages = await getEnrichedMatrixMessagesFromRoom(matrixClient, room, {
      loadHistory: true,
    })
    const needle = normalizeMessageSearchText(trimmedQuery)
    if (!needle) {
      searchDebugLog('matrix · empty needle after normalize', {
        roomId,
        query: trimmedQuery,
      })
      return []
    }

    let deletedSkipped = 0
    let emptyBodySkipped = 0
    const candidates: Array<{ preview: string; normalized: string; includes: boolean }> = []

    const matchedMessages = messages.filter(message => {
      if (message.is_deleted) {
        deletedSkipped += 1
        return false
      }

      const rawBody = String(message.content || '')
      const body = normalizeMessageSearchText(rawBody)
      if (!body) {
        emptyBodySkipped += 1
        return false
      }

      const includes = body.includes(needle)
      if (candidates.length < 12) {
        candidates.push({
          preview: summarizeSearchBodyForLog(rawBody),
          normalized: summarizeSearchBodyForLog(body),
          includes,
        })
      }

      return includes
    })

    const localHits = matchedMessages.map(message => messageToSearchHit(message, myUserId))
    const serverHits = await searchMatrixMessagesViaServer(
      matrixClient,
      roomId,
      trimmedQuery,
      needle,
      myUserId,
    )

    const byEventId = new Map<string, MatrixMessageSearchHit>()
    for (const hit of [...localHits, ...serverHits]) {
      if (!byEventId.has(hit.matrixEventId)) {
        byEventId.set(hit.matrixEventId, hit)
      }
    }

    const hits = [...byEventId.values()]
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))

    const reason = hits.length > 0
      ? null
      : messages.length === 0
        ? 'no-messages-in-room-timeline'
        : deletedSkipped === messages.length
          ? 'all-messages-deleted'
          : emptyBodySkipped === messages.length - deletedSkipped
            ? 'all-bodies-empty'
            : 'needle-not-found-in-loaded-bodies'

    searchDebugLog('matrix · done', {
      roomId,
      roomName: room.name || null,
      query: trimmedQuery,
      needle,
      timelineEventsBeforeLoad: timelineBefore,
      timelineEventsAfterLoad: room.getLiveTimeline().getEvents().length,
      messagesLoaded: messages.length,
      deletedSkipped,
      emptyBodySkipped,
      localHitsCount: localHits.length,
      serverHitsCount: serverHits.length,
      hitsCount: hits.length,
      durationMs: Math.round(performance.now() - startedAt),
      reason,
      sampleBodies: candidates,
      hitsPreview: hits.slice(0, 8).map(hit => ({
        matrixEventId: hit.matrixEventId,
        preview: summarizeSearchBodyForLog(hit.content),
        senderName: hit.senderName,
      })),
    })

    return hits
  })
}

const FORWARD_PAYLOAD_KEYS = [
  'msgtype',
  'body',
  'format',
  'formatted_body',
  'url',
  'info',
  'filename',
  'file',
] as const

async function resolveSourceEventContent(
  matrixClient: MatrixClient,
  sourceRoomId: string,
  sourceEventId: string,
): Promise<Record<string, unknown> | null> {
  const room = matrixClient.getRoom(sourceRoomId)
  const fromTimeline = room?.findEventById(sourceEventId)
  if (fromTimeline) {
    return resolveMatrixMessageContent(fromTimeline)
  }

  try {
    const raw = await matrixClient.fetchRoomEvent(sourceRoomId, sourceEventId)
    const mapped = matrixClient.getEventMapper()(raw)
    return resolveMatrixMessageContent(mapped)
  } catch {
    return null
  }
}

export async function sendMatrixForwardedMessage(
  destinationRoomId: string,
  input: {
    forwardId: string
    sourceRoomId: string
    sourceEventId: string
    snapshot: {
      body: string
      msgtype: string
      original_sender_display: string
    }
  },
): Promise<{ eventId: string }> {
  return withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    const sourceContent = await resolveSourceEventContent(
      matrixClient,
      input.sourceRoomId,
      input.sourceEventId,
    )

    const payload: Record<string, unknown> = {
      msgtype: input.snapshot.msgtype || MsgType.Text,
      body: input.snapshot.body,
    }

    if (sourceContent) {
      for (const key of FORWARD_PAYLOAD_KEYS) {
        if (sourceContent[key] !== undefined) {
          payload[key] = sourceContent[key]
        }
      }
      if (!payload.body) {
        payload.body = input.snapshot.body
      }
      if (!payload.msgtype) {
        payload.msgtype = input.snapshot.msgtype || MsgType.Text
      }
    }

    payload[MATRIX_FORWARD_CONTENT_KEY] = {
      forward_id: input.forwardId,
      source_room_id: input.sourceRoomId,
      source_event_id: input.sourceEventId,
      ...(input.snapshot.original_sender_display
        ? { original_sender_display: input.snapshot.original_sender_display }
        : {}),
    }

    const response = await matrixClient.sendMessage(destinationRoomId, null, payload as any)
    return { eventId: response.event_id }
  })
}

export async function leaveAndForgetMatrixRoom(matrixRoomId: string): Promise<void> {
  const matrixClient = await startMatrixClient()
  const room = matrixClient.getRoom(matrixRoomId)
  const membership = room?.getMyMembership()

  if (membership === 'join' || membership === 'invite') {
    try {
      await matrixClient.leave(matrixRoomId)
      await waitUntilNotJoined(matrixClient, matrixRoomId)
    } catch (error) {
      // Уже не в комнате / M_FORBIDDEN — всё равно forget локально.
      chatDebugLog('leaveAndForgetMatrixRoom · leave skipped', {
        roomId: matrixRoomId,
        membership,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  try {
    await matrixClient.forget(matrixRoomId)
  } catch {
  }

  rememberForgottenRoom(matrixRoomId)
}

export async function deleteMatrixChat(
  chat: IChat,
  options?: { deleteForPeer?: boolean },
): Promise<void> {
  const matrixClient = await startMatrixClient()
  const matrixRoomId = resolveMatrixRoomId(chat, matrixClient)
  if (!matrixRoomId) {
    throw new Error('Не найден идентификатор комнаты чата')
  }

  const isDialog = chat.type === 'dialog' || chat.type === 'direct'
  const room = matrixClient.getRoom(matrixRoomId)
  const membership = room?.getMyMembership()
  const canNotifyPeer = membership === 'join'

  // Сигнал пиру можно слать только из join. Ghost-диалоги (есть в REST, нет в Matrix)
  // иначе падают с M_FORBIDDEN «not in room» и не удаляются из UI.
  if (options?.deleteForPeer && isDialog && canNotifyPeer) {
    try {
      await matrixClient.sendEvent(matrixRoomId, MATRIX_CHAT_DELETE_FOR_PEER_EVENT, {})
    } catch (error) {
      chatDebugLog('deleteMatrixChat · peer signal failed', {
        roomId: matrixRoomId,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  } else if (options?.deleteForPeer && isDialog && !canNotifyPeer) {
    chatDebugLog('deleteMatrixChat · skip peer signal (not in room)', {
      roomId: matrixRoomId,
      membership: membership ?? null,
      hasRoom: Boolean(room),
    })
  }

  await leaveAndForgetMatrixRoom(matrixRoomId)
}

function parseMatrixMemberDisplayName(name: string): {
  first_name: string
  last_name: string
  middle_name: string
} {
  const parts = name.trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) {
    return { first_name: '', last_name: '', middle_name: '' }
  }

  if (parts.length === 1) {
    return { first_name: parts[0], last_name: '', middle_name: '' }
  }

  return {
    last_name: parts[0],
    first_name: parts[1],
    middle_name: parts.slice(2).join(' '),
  }
}

function matrixMemberIdentifier(mxid: string): string {
  const localpart = mxid.split(':')[0].slice(1)
  return phoneFromMatrixLocalpart(localpart) || localpart
}

export async function getMatrixRoomAvatarUrl(roomId: string): Promise<string | null> {
  const matrixClient = await startMatrixClient()
  const room = matrixClient.getRoom(roomId)
  const mxcAvatar = room?.getMxcAvatarUrl?.()

  if (!mxcAvatar) {
    return null
  }

  return toAuthenticatedMatrixMediaUrl(matrixClient, mxcAvatar, 96, 96, 'crop')
}

export type FetchMatrixRoomMembersMode = 'light' | 'full'

// Room members: `light` = state only; `full` = profile + presence (expensive).
export async function fetchMatrixRoomMembers(
  roomId: string,
  options?: { mode?: FetchMatrixRoomMembersMode },
): Promise<ChatMemberResponse[]> {
  const mode = options?.mode ?? 'light'
  const matrixClient = await startMatrixClient()
  const room = matrixClient.getRoom(roomId)

  if (!room) {
    return []
  }

  await room.loadMembersIfNeeded()

  const creator = room.getCreator?.() ?? ''
  const seenMemberIds = new Set<string>()
  const roomMembers = room.getMembers().filter(member => {
    if (member.membership !== 'join' && member.membership !== 'invite') {
      return false
    }
    if (seenMemberIds.has(member.userId)) {
      return false
    }
    if (isMatrixHiddenUser(member.userId)) {
      return false
    }
    seenMemberIds.add(member.userId)
    return true
  })

  const members = await Promise.all(
    roomMembers.map(async member => {
      let displayName = member.name || member.rawDisplayName || member.userId
      let avatarUrl = ''

      if (mode === 'full') {
        const profile = await resolveMatrixSenderProfile(matrixClient, member.userId, room)
        displayName = profile.displayName
        avatarUrl = profile.avatarUrl || ''
      } else {
        const mxcAvatar = member.getMxcAvatarUrl?.() ?? null
        if (mxcAvatar) {
          avatarUrl = toAuthenticatedMatrixMediaUrl(matrixClient, mxcAvatar, 96, 96, 'crop') || ''
        }
        if (displayName.startsWith('@')) {
          displayName = formatMatrixDisplayName(member.userId)
        }
      }

      const names = parseMatrixMemberDisplayName(displayName)
      const powerLevel = member.powerLevel ?? 0
      const isAdmin = member.userId === creator || powerLevel >= 50
      const joinedEvent = member.events?.member
      const joinedAt = joinedEvent?.getTs()
        ? new Date(joinedEvent.getTs()).toISOString()
        : new Date().toISOString()

      return {
        user: {
          id: matrixMemberIdentifier(member.userId),
          first_name: names.first_name,
          last_name: names.last_name,
          middle_name: names.middle_name,
          avatar: avatarUrl,
          department_name: '',
          position_name: '',
        },
        user_uuid: member.userId,
        avatar: avatarUrl,
        online: getCachedMatrixPresence(member.userId) ?? false,
        is_admin: isAdmin,
        joined_at: joinedAt,
        membership: member.membership === 'invite' ? 'invite' : 'join',
      } satisfies ChatMemberResponse
    }),
  )

  const presenceMxids = roomMembers
    .filter(member => member.membership === 'join')
    .map(member => member.userId)

  if (mode === 'full') {
    await trackMatrixPresence(presenceMxids)
    return members.map(member => {
      const mxid = member.user_uuid
      if (!mxid) {
        return member
      }
      const online = getCachedMatrixPresence(mxid)
      return online === undefined || member.online === online
        ? member
        : { ...member, online }
    })
  }

  void trackMatrixPresence(presenceMxids)
  return members
}

export async function kickMatrixRoomMember(
  roomId: string,
  mxidOrUserId: string,
): Promise<void> {
  const matrixClient = await startMatrixClient()
  let mxid = mxidOrUserId

  if (!mxid.startsWith('@')) {
    const resolved = await resolveMatrixUserId(
      matrixClient,
      mxidOrUserId,
      getCurrentMatrixUserContext(),
    )

    if (!resolved) {
      throw new Error('Пользователь не найден в чате')
    }

    mxid = resolved
  }

  const room = matrixClient.getRoom(roomId)
  const memberBefore = room?.getMember(mxid)
  if (!isActiveMatrixMembership(memberBefore?.membership)) {
    return
  }

  try {
    await matrixClient.kick(roomId, mxid, '')
  } catch (error) {
    const memberAfter = matrixClient.getRoom(roomId)?.getMember(mxid)
    if (
      getMatrixHttpStatus(error) === 403
      && !isActiveMatrixMembership(memberAfter?.membership)
    ) {
      return
    }

    throw error
  }
}

export { sendMatrixReadReceiptForRoom } from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'
